import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useAuthenticatedUser } from '@treams/treams-hooks';
import { GridIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Card, Container, Empty, Jumbo, Separator, Skeleton, Subheading } from '@treams/treams-ui';

import TalentReviewGroupCard from './components/TalentReviewGroupCard';
import useBatchedTalentReviewGroups from './hooks/useBatchedTalentReviewGroups';
import { groupTalentReviewIndexMoments } from './talentReviewIndexMoments';

export default function TalentReviews() {
	const { t } = useTranslation();

	const authenticatedUser = useAuthenticatedUser();

	const allGroups = useBatchedTalentReviewGroups();

	// The viewer's own cycle moments, matched to groups client-side: one
	// request instead of `Group.talentReviewCycleMoment` per card.
	const momentsQuery = graphql.useTalentReviewIndexMomentsQuery({
		variables: { userID: authenticatedUser.id },
		errorPolicy: 'all',
	});

	const momentsByGroup = useMemo(
		() => groupTalentReviewIndexMoments(momentsQuery.data?.user?.moments ?? []),
		[momentsQuery.data?.user?.moments],
	);

	const activitiesQuery = graphql.useTalentReviewLatestActivitiesQuery({
		errorPolicy: 'all',
	});

	const activityByGroup = useMemo(
		() =>
			new Map(
				(activitiesQuery.data?.talentReviewLatestActivities ?? []).flatMap((activity) =>
					activity ? [[activity.group.id, activity] as const] : [],
				),
			),
		[activitiesQuery.data?.talentReviewLatestActivities],
	);

	const groups = useMemo(
		() =>
			(allGroups ?? [])
				.filter((group) => group.viewerCanViewTalentReviews)
				.sort((a, b) => a.name.localeCompare(b.name)),
		[allGroups],
	);

	const loading = !allGroups || (activitiesQuery.loading && !activitiesQuery.data);

	return (
		<>
			<Jumbo
				title={t('talentReviews.v2.index.title')}
				description={t('talentReviews.v2.index.description')}
				footer={
					<Container>
						<Separator />
					</Container>
				}
			/>

			<Container mt={4}>
				{loading ? (
					<Box mt={4}>
						<Subheading.Skeleton width={220} />
						<Card mt={3} p={0}>
							<Skeleton.Repeat count={6}>
								<TalentReviewGroupCard.Skeleton />
							</Skeleton.Repeat>
						</Card>
					</Box>
				) : groups.length > 0 ? (
					<Box mt={4}>
						<Subheading>{t('talentReviews.v2.index.groupsTitle')}</Subheading>
						<Card mt={3} p={0}>
							{groups.map((group) => (
								<TalentReviewGroupCard
									key={group.id}
									group={group}
									activity={activityByGroup.get(group.id)}
									cycleMoment={momentsByGroup.get(group.id)}
								/>
							))}
						</Card>
					</Box>
				) : (
					<Box mt={5}>
						<Empty icon={<GridIcon />} title={t('talentReviews.v2.index.notFound')} small />
					</Box>
				)}
			</Container>
		</>
	);
}
